import { useAuth0User } from '@hooks/useAuth0User';
import { useEntitlements } from '@hooks/useEntitlements';
import { useIsAppView } from '@hooks/useIsAppView';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import type { SnackAdEligibility } from '@lib/ads/snack';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import { isPaidMember } from '@lib/utils';

/**
 * What decides whether this visitor gets Snack Media's ads. See
 * shouldLoadSnackAds and getAdSpaceChange for the rules.
 *
 * useEntitlements is read directly, against its own advice, because the rules
 * need to know whether the live membership has arrived, not just what it says.
 */
export const useSnackAdEligibility = (): SnackAdEligibility => {
  const isAdvertisingLive = usePostHogFeatureFlag(
    PostHogFeatureFlags.snackAdvertisingIsLive,
    false
  );
  const { isAppView, isLoading: isAppViewLoading } = useIsAppView();
  const { user, error: authError, isLoading: isUserLoading } = useAuth0User();
  const { entitlements } = useEntitlements();

  return {
    isAdvertisingLive,
    isAppViewLoading,
    isAppView,
    isUserLoading,
    hasAuthError: Boolean(authError),
    isLoggedIn: Boolean(user),
    // A degraded answer is ffh-backend's FREE fallback for a failed lookup,
    // which useAuth0User ignores, so it is not live membership.
    hasLiveMembership: Boolean(entitlements && !entitlements.isDegraded),
    isPaidMember: isPaidMember(user)
  };
};
